import { registryLookup } from 'components/knz/embeds/registry';
import { parseKnz } from 'components/knz/parser';
import { KnzNode } from 'components/knz/types';
import { AnswerCodeInput } from 'stores/TrainingAdminStoreAdapter';
import { WorkSpaceNode } from 'stores/WorkSpaceStoreAdapter';

/**
 * knz 設問の構造化データ抽出（フロントがパースする・R-11）。
 *
 * バックエンドは knz をパースしないため、workspace / 設問登録画面がここで
 * knz をパースして title / AnswerCode / Expected を抽出し、
 * 反映API（API6316）や登録API（API6311・KNZ方式）に構造化データとして渡す。
 *
 * knz スキーマ（議題6 / D6-B）:
 * - AnswerCode ブロック: `file`（正解コードのファイルパス）＋ `expected`（Expected の id リスト `- id`）
 * - Expected ブロック: `id` ＋ `sourceFile`（由来実行履歴のファイルパス）＋ `sourceSeq`（由来実行履歴の連番）
 * - AnswerCode / Expected は記事本文には描画されない（設問プレフィル用メタデータ）
 *
 * knz には可読性のためファイル「パス」（例: /src/Main.java）を書く。
 * バックエンドは fileId で受け取るため、フロントがワークスペースツリーから
 * パス → fileId を解決してから反映APIに渡す。
 * 後方互換として、パスで解決できない場合は値を fileId とみなしてフォールバックする。
 */

export type ParsedQuestionKnz = {
  /** frontmatter の title（必須） */
  title: string;
  /** 正解コード（fileId＋期待値の由来） */
  answerCodes: AnswerCodeInput[];
  /** パース時のエラー（構文エラー・必須欠落・パス解決失敗）。空なら反映可能 */
  errors: string[];
};

/** knz に書かれたパス（または fileId）を workspace の fileId へ解決する関数。 */
export type FileRefResolver = (pathOrId: string) => string | null;

const asString = (v: string | string[] | undefined): string => {
  if (v == null) return '';
  return Array.isArray(v) ? v.join('') : v;
};

/**
 * ワークスペースツリーからパス/ fileId 解決関数を作る。
 * - path（例 /src/Main.java）に一致すれば その fileId
 * - path で見つからなければ、値が既存の fileId ならそのまま（後方互換）
 * - どちらでもなければ null（未解決）
 */
export const buildFileRefResolver = (nodes: WorkSpaceNode[]): FileRefResolver => {
  const pathToId = new Map<string, string>();
  const fileIds = new Set<string>();
  nodes.forEach((n) => {
    if (n.type === 'FILE' && n.fileId) {
      fileIds.add(n.fileId);
      if (n.path) pathToId.set(n.path, n.fileId);
    }
  });
  return (pathOrId: string) => {
    const key = pathOrId.trim();
    if (pathToId.has(key)) return pathToId.get(key) as string;
    if (fileIds.has(key)) return key; // 後方互換: fileId 直指定
    return null;
  };
};

/** ノード配列から name 一致のノードを再帰収集する。 */
const collectByName = (nodes: KnzNode[], name: string): KnzNode[] => {
  const result: KnzNode[] = [];
  const walk = (ns: KnzNode[]) => {
    ns.forEach((n) => {
      if (n.name === name) result.push(n);
      if (n.children.length > 0) walk(n.children);
    });
  };
  walk(nodes);
  return result;
};

/**
 * knz ソースをパースして設問の構造化データを抽出する。
 * @param source knz ソース
 * @param resolver パス/ fileId → fileId 解決関数（省略時は解決せず値をそのまま fileId 扱い）
 */
export const parseQuestionKnz = (source: string, resolver?: FileRefResolver): ParsedQuestionKnz => {
  const result = parseKnz(source, registryLookup);
  const errors: string[] = [];

  if (!result.frontmatter) {
    errors.push('frontmatter（title / author）が不足しています');
  }
  result.diagnostics
    .filter((d) => d.severity === 'error')
    .forEach((d) => errors.push(`行 ${d.line}: ${d.message}`));

  const title = result.frontmatter?.title ?? '';

  // resolver がなければ値をそのまま返す（テスト・後方互換用）
  const resolve = (raw: string): string | null => (resolver ? resolver(raw) : raw);

  // Expected ブロックを id で引けるように収集する（本文に描画されないルート）
  const expectedById = new Map<string, KnzNode>();
  collectByName(result.roots, 'Expected').forEach((ex) => {
    if (ex.id) expectedById.set(ex.id, ex);
  });

  const asIdList = (v: string | string[] | undefined): string[] => {
    if (v == null) return [];
    return Array.isArray(v)
      ? v.map((s) => s.trim()).filter(Boolean)
      : v
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
  };

  // AnswerCode ブロックを収集（expected の id リストで Expected を参照）
  const answerNodes = collectByName(result.roots, 'AnswerCode');
  const answerCodes: AnswerCodeInput[] = answerNodes.map((ac) => {
    const fileRef = asString(ac.params.file).trim();
    let fileId = '';
    if (!fileRef) {
      errors.push('AnswerCode に file（正解コードのファイルパス）が指定されていません');
    } else {
      const resolved = resolve(fileRef);
      if (!resolved) {
        errors.push(`AnswerCode の file が見つかりません: ${fileRef}`);
      } else {
        fileId = resolved;
      }
    }

    // expected（id リスト）→ Expected ブロックを解決
    const expectedIds = asIdList(ac.params.expected);
    if (expectedIds.length === 0) {
      errors.push(
        `AnswerCode（${fileRef || 'file 未指定'}）の expected（期待値の id）がありません`,
      );
    }
    const expectedList = expectedIds.map((eid) => {
      const ex = expectedById.get(eid);
      if (!ex) {
        errors.push(`Expected が見つかりません（id: ${eid}）`);
        return { sourceFileId: '', sourceSeq: -1 };
      }
      const sourceRef = asString(ex.params.sourceFile).trim();
      const seqStr = asString(ex.params.sourceSeq).trim();
      const sourceSeq = Number(seqStr);
      let sourceFileId = '';
      if (!sourceRef) {
        errors.push(`Expected（id: ${eid}）の sourceFile が指定されていません`);
      } else {
        const resolved = resolve(sourceRef);
        if (!resolved) {
          errors.push(`Expected（id: ${eid}）の sourceFile が見つかりません: ${sourceRef}`);
        } else {
          sourceFileId = resolved;
        }
      }
      if (seqStr === '' || Number.isNaN(sourceSeq)) {
        errors.push(`Expected（id: ${eid}）の sourceSeq が不正です`);
      }
      return { sourceFileId, sourceSeq };
    });
    return { fileId, expectedList };
  });

  if (answerCodes.length === 0) {
    errors.push('AnswerCode（正解コード）が1つもありません');
  }

  return { title, answerCodes, errors };
};
